import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarOff, Clock, Plus, Trash2 } from "lucide-react";
import { AdminWeeklyHours } from "@/components/AdminWeeklyHours";

const formatDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

export const AdminScheduleEditor = ({ settings, saving, onSave }) => {
  const [holiday, setHoliday] = useState({ date: "", name: "" });
  const [special, setSpecial] = useState({ date: "", open: 12, close: 22 });

  const holidays = settings.holidays || [];
  const specialHours = settings.special_hours || [];

  const addHoliday = () => {
    if (!holiday.date) return;
    if (holidays.some((h) => h.date === holiday.date)) {
      toast.error("Эта дата уже добавлена");
      return;
    }
    onSave({ holidays: [...holidays, { date: holiday.date, name: holiday.name.trim() }] });
    setHoliday({ date: "", name: "" });
  };

  const removeHoliday = (date) => onSave({ holidays: holidays.filter((h) => h.date !== date) });

  const addSpecial = () => {
    const open = Number(special.open);
    const close = Number(special.close);
    if (!special.date) return;
    if (Number.isNaN(open) || Number.isNaN(close) || open < 0 || open > 23 || close < 1 || close > 24) {
      toast.error("Часы должны быть в диапазоне 0–24");
      return;
    }
    if (close <= open) {
      toast.error("Время закрытия должно быть позже открытия");
      return;
    }
    const rest = specialHours.filter((s) => s.date !== special.date);
    onSave({ special_hours: [...rest, { date: special.date, open, close }] });
    setSpecial({ date: "", open: 12, close: 22 });
  };

  const removeSpecial = (date) => onSave({ special_hours: specialHours.filter((s) => s.date !== date) });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-schedule-editor">
      <AdminWeeklyHours weekly={settings.weekly_hours} saving={saving} onSave={onSave} />
      <Card className="p-5">
        <h3 className="font-bold mb-1 flex items-center gap-2">
          <CalendarOff className="w-5 h-5 text-red-600" />
          Праздничные выходные
        </h3>
        <p className="text-sm text-gray-600 mb-4">В эти дни ресторан закрыт, заказы и предзаказы не принимаются.</p>

        {holidays.length === 0 ? (
          <p className="text-sm text-gray-500 mb-4" data-testid="holidays-empty">Нет праздничных дней</p>
        ) : (
          <ul className="space-y-2 mb-4" data-testid="holidays-list">
            {holidays.map((h) => (
              <li key={h.date} className="flex items-center justify-between gap-3 p-2.5 rounded-md border bg-red-50 border-red-200" data-testid="holiday-item">
                <div className="min-w-0">
                  <span className="font-semibold">{formatDate(h.date)}</span>
                  {h.name && <span className="text-gray-700 ml-2 truncate">{h.name}</span>}
                </div>
                <Button size="sm" variant="ghost" disabled={saving} onClick={() => removeHoliday(h.date)} className="text-red-600 hover:bg-red-100" data-testid="holiday-remove-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <Input type="date" value={holiday.date} onChange={(e) => setHoliday({ ...holiday, date: e.target.value })} className="sm:w-44" data-testid="holiday-date-input" />
          <Input placeholder="Название (необязательно)" value={holiday.name} onChange={(e) => setHoliday({ ...holiday, name: e.target.value })} data-testid="holiday-name-input" />
          <Button onClick={addHoliday} disabled={saving || !holiday.date} variant="outline" data-testid="holiday-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Добавить
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-bold mb-1 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-600" />
          Особые часы работы
        </h3>
        <p className="text-sm text-gray-600 mb-4">Для конкретной даты заменяют обычный график (часы 0–24, 24 = полночь).</p>

        {specialHours.length === 0 ? (
          <p className="text-sm text-gray-500 mb-4" data-testid="special-hours-empty">Нет особых дней</p>
        ) : (
          <ul className="space-y-2 mb-4" data-testid="special-hours-list">
            {specialHours.map((s) => (
              <li key={s.date} className="flex items-center justify-between gap-3 p-2.5 rounded-md border bg-amber-50 border-amber-200" data-testid="special-hours-item">
                <div>
                  <span className="font-semibold">{formatDate(s.date)}</span>
                  <span className="text-gray-700 ml-2">{String(s.open).padStart(2, "0")}:00 – {s.close === 24 ? "00:00" : `${String(s.close).padStart(2, "0")}:00`}</span>
                </div>
                <Button size="sm" variant="ghost" disabled={saving} onClick={() => removeSpecial(s.date)} className="text-red-600 hover:bg-red-100" data-testid="special-hours-remove-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <Input type="date" value={special.date} onChange={(e) => setSpecial({ ...special, date: e.target.value })} className="sm:w-44" data-testid="special-date-input" />
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={23} value={special.open} onChange={(e) => setSpecial({ ...special, open: e.target.value })} className="w-20" data-testid="special-open-input" />
            <span className="text-gray-500">—</span>
            <Input type="number" min={1} max={24} value={special.close} onChange={(e) => setSpecial({ ...special, close: e.target.value })} className="w-20" data-testid="special-close-input" />
          </div>
          <Button onClick={addSpecial} disabled={saving || !special.date} variant="outline" data-testid="special-hours-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Добавить
          </Button>
        </div>
      </Card>
    </div>
  );
};
